import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToExecutiveInsightCards } from "@/utils/mistralInsightMappers";
import {
  AlertTriangle,
  ShieldCheck,
  BarChart3,
  TrendingUp,
} from "lucide-react";
import { BlockLink } from "@/components/DrilldownLink";
import {
  buildCtReadinessScore,
  buildCtReadinessGaugeStyle,
  buildCtHeroHeadline,
  buildCtHeroNarrative,
  buildCtStatusChips,
  buildCtPrimaryAction,
  buildCtExecutiveKpis,
  buildCtComplianceRings,
  buildCtGenderDonut,
  buildCtBlockBars,
  buildCtExecutiveInsights,
  buildCtBlockTableRows,
  buildCtTableMeta,
  buildCTTeacherExecutiveMarkdown,
} from "@/utils/ctteacherExecutiveDerived";
import { toTitleCase } from "../utils/toTitleCase";

const INSIGHT_ICONS = {
  red: AlertTriangle,
  green: ShieldCheck,
  purple: BarChart3,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const CTTeacherExecutiveTab = ({ overview = {}, blockData = [], genderData = [], onNavigateTab }) => {
  const hasData = Boolean(overview?.total_teachers);

  const buildLocalMarkdown = useCallback(
    () => buildCTTeacherExecutiveMarkdown(overview, blockData),
    [overview, blockData]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ t: overview?.total_teachers, c: overview?.ctet_pct }),
    [overview?.total_teachers, overview?.ctet_pct]
  );
  const {
    loading: aiLoading,
    pickHeadline,
    pickFromBucket,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "ctteacher-executive",
    title: "CT Teacher Executive Overview",
    buildLocalMarkdown,
    context: { overview, blockData },
    enabled: hasData,
    scopeKey,
  });

  const readiness = useMemo(() => buildCtReadinessScore(overview), [overview]);
  const gaugeStyle = useMemo(() => buildCtReadinessGaugeStyle(readiness), [readiness]);
  const localHeadline = useMemo(() => buildCtHeroHeadline(overview), [overview]);
  const localNarrative = useMemo(() => buildCtHeroNarrative(overview), [overview]);
  const headline = useMemo(
    () => (aiLoading ? localHeadline : pickHeadline(localHeadline)),
    [aiLoading, pickHeadline, localHeadline]
  );
  const narrative = useMemo(
    () => (aiLoading ? localNarrative : pickFromBucket(localNarrative, "insights", 1)),
    [aiLoading, pickFromBucket, localNarrative]
  );
  const statusChips = useMemo(() => {
    const chips = buildCtStatusChips(overview);
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "purple" }, ...chips];
    }
    return chips;
  }, [overview, isMistral, aiLoading]);
  const localPrimaryAction = useMemo(
    () => buildCtPrimaryAction(overview, blockData),
    [overview, blockData]
  );
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const kpis = useMemo(() => buildCtExecutiveKpis(overview), [overview]);
  const rings = useMemo(() => buildCtComplianceRings(overview), [overview]);
  const genderDonut = useMemo(
    () => buildCtGenderDonut(overview, genderData),
    [overview, genderData]
  );
  const blockBars = useMemo(() => buildCtBlockBars(blockData), [blockData]);
  const localInsights = useMemo(
    () => buildCtExecutiveInsights(overview, blockData),
    [overview, blockData]
  );
  const insights = useMemo(
    () => pickMapped(localInsights, "insights", bulletsToExecutiveInsightCards),
    [pickMapped, localInsights]
  );
  const tableRows = useMemo(() => buildCtBlockTableRows(blockData), [blockData]);
  const tableMeta = useMemo(() => buildCtTableMeta(overview, blockData), [overview, blockData]);

  return (
    <div className="ctex-exec-tab" data-testid="ctteacher-executive">
      <section className="ctex-hero-card" id="ctex-hero">
        <div className="ctex-hero-layout">
          <div>
            <div className="ctex-health-main">
              <div
                className="ctex-gauge"
                style={gaugeStyle}
                aria-label={`Teacher readiness index ${readiness} percent`}
              >
                <div>
                  <strong>{readiness}</strong>
                  <span>Readiness</span>
                </div>
              </div>
              <div className="ctex-health-copy">
                <h2>{toTitleCase(headline)}</h2>
                <p>{narrative}</p>
                <div className="ctex-status-row">
                  {statusChips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`ctex-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="ctex-summary-note">
              <div className="ctex-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="ctex-metric-grid" aria-label="Teacher KPIs">
            {kpis.map((card) => (
              <article key={card.label} className="ctex-metric-card">
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span className={card.subClass}>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="ctex-section-title" id="ctex-compliance">
        <div>
          <h2>Compliance & Certification Health</h2>
          <p>
            Separated from other charts so executives can quickly distinguish data quality success
            from training and certification risk.
          </p>
        </div>
        <button type="button" className="ctex-section-action" onClick={() => scrollToId("ctex-blocks")}>
          View block ranking →
        </button>
      </div>

      <section className="ctex-score-grid">
        {rings.map((ring) => (
          <article key={ring.label} className="ctex-ring-card">
            <div
              className={`ctex-ring ${ring.ringClass}`.trim()}
              style={{ "--pct": `${ring.value}%` }}
            >
              <strong>{ring.display}</strong>
            </div>
            <h3>{toTitleCase(ring.label)}</h3>
            <p>{ring.description}</p>
          </article>
        ))}
      </section>

      <div className="ctex-section-title" id="ctex-workforce">
        <div>
          <h2>Workforce Composition and Block Load</h2>
          <p>
            The redesigned chart area uses two large panels instead of many equal-weight cards,
            improving hierarchy and readability.
          </p>
        </div>
        <button
          type="button"
          className="ctex-section-action"
          onClick={() => onNavigateTab?.("insights")}
        >
          View insights →
        </button>
      </div>

      <section className="ctex-analytics-grid">
        <article className="ctex-panel">
          <div className="ctex-panel-head">
            <div>
              <h3>Gender Distribution</h3>
              <p>Teacher workforce by gender with GPI retained as a key executive indicator.</p>
            </div>
            <span className="ctex-pill">GPI {genderDonut.gpi}</span>
          </div>
          <div className="ctex-gender-card-body">
            <div className="ctex-donut" style={genderDonut.style} aria-hidden="true">
              <div className="ctex-donut-center">
                <div>
                  <strong>{genderDonut.total.toLocaleString("en-IN")}</strong>
                  <span>Records</span>
                </div>
              </div>
            </div>
            <div className="ctex-legend">
              {genderDonut.legend.map((row) => (
                <div key={row.label} className="ctex-legend-row">
                  <span className="ctex-legend-label">
                    <i className={`ctex-dot ${row.dotClass}`.trim()} />
                    {row.label}
                  </span>
                  <b>
                    {row.value.toLocaleString("en-IN")}
                    {!row.hidePct ? ` · ${row.pct.toFixed(1)}%` : ""}
                  </b>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="ctex-panel">
          <div className="ctex-panel-head">
            <div>
              <h3>Teachers by Block · Top 10</h3>
              <p>
                {blockBars[0]?.block
                  ? `${blockBars[0].block} and ${blockBars[1]?.block || "peer blocks"} are the largest load centres and should receive first-level operational monitoring.`
                  : "Block load ranking will appear once block-wise data is available."}
              </p>
            </div>
            <span className="ctex-pill green">Load view</span>
          </div>
          <div className="ctex-bar-list" aria-label="Teachers by block bar chart">
            {blockBars.map((row) => (
              <div key={row.block} className="ctex-hbar-row">
                <div className="ctex-hbar-label">{row.block}</div>
                <div className="ctex-hbar-track">
                  <div className="ctex-hbar" style={{ width: `${row.width}%` }} />
                </div>
                <div className="ctex-hbar-value">{row.display}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="ctex-section-title" id="ctex-insights">
        <div>
          <h2>Executive Insight Cards</h2>
          <p>Three decision cards summarize where leadership attention should go next.</p>
        </div>
        <button type="button" className="ctex-section-action" onClick={() => scrollToId("ctex-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="ctex-insights-grid">
        {insights.map((card) => {
          const Icon = INSIGHT_ICONS[card.iconClass] ?? TrendingUp;
          return (
            <article key={card.id} className="ctex-insight-card">
              <div className={`ctex-icon-box ${card.iconClass}`.trim()}>
                <Icon size={22} />
              </div>
              <div>
                <h3>{toTitleCase(card.title)}</h3>
                <p>{card.body}</p>
              </div>
            </article>
          );
        })}
      </section>

      <section className="ctex-panel ctex-table-card" id="ctex-blocks">
        <div className="ctex-table-head">
          <div>
            <h3>Block-Wise Teacher Distribution</h3>
            <p>
              Full block table retained, but moved below executive insights to reduce first-fold
              clutter.
            </p>
          </div>
          <span className="ctex-pill">
            {tableMeta.blockCount} Blocks · {tableMeta.recordCount.toLocaleString("en-IN")} Records
          </span>
        </div>
        <div className="ctex-table-wrap">
          <table aria-label="Block-wise Teacher Distribution">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th className="num">Teachers</th>
                <th className="num">Schools</th>
                <th className="num">Avg/School</th>
                <th className="num">Female %</th>
                <th className="num">GPI</th>
                <th className="num">Aadhaar %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.blockName}>
                  <td className="ctex-rank">{row.rank}</td>
                  <td>
                    <BlockLink blockCode={row.blockCode}>{row.blockName}</BlockLink>
                  </td>
                  <td className="num">{row.teachers?.toLocaleString("en-IN")}</td>
                  <td className="num">{row.schools}</td>
                  <td className="num">{row.avgSchool}</td>
                  <td className="num">{row.femalePct}%</td>
                  <td className="num">{row.gpi}</td>
                  <td className="num">
                    <span className="ctex-progress-cell">
                      {row.aadhaarDisplay}
                      <span className={`ctex-mini-track ${row.trackClass}`.trim()}>
                        <i style={{ width: `${row.aadhaarPct}%` }} />
                      </span>
                    </span>
                  </td>
                  <td>
                    <span className={`ctex-tag ${row.statusClass}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default CTTeacherExecutiveTab;
